import { useEffect } from 'react';
import { useReactFlow } from '@xyflow/react';
import type { MindmapLayout } from './sideTree';

/** Space kept between the roadmap and the edge of the pane. */
const PAD = 24;
/** Never open above 100%: the pills are designed at 1x. */
const MAX_OPEN_ZOOM = 1;
const MIN_ZOOM = 0.15;

/** The Fit button's framing: the whole roadmap, top to bottom. */
export const FIT_ALL = { padding: 0.08, maxZoom: MAX_OPEN_ZOOM };

function bounds(layout: MindmapLayout, maxDepth = Infinity) {
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  for (const n of layout.nodes) {
    if (n.depth > maxDepth) continue;
    minX = Math.min(minX, n.x - n.w / 2);
    maxX = Math.max(maxX, n.x + n.w / 2);
    minY = Math.min(minY, n.y - n.h / 2);
    maxY = Math.max(maxY, n.y + n.h / 2);
  }
  return { minX, maxX, minY, maxY };
}

/** Below this zoom the 12px labels stop being readable. */
export const READABLE_ZOOM = 0.8;
/** Where a map too wide to fit readably opens: just under 1x. */
export const OPEN_ZOOM = 0.85;

export interface Viewport {
  x: number;
  y: number;
  zoom: number;
}

/**
 * The roadmap's opening view, for a pane `width` x `height`. Fit its WIDTH
 * (never above 100%), centre it horizontally, and start at the main node. A
 * roadmap is tall by design, so fitting the whole thing would shrink it back
 * to the unreadable zoom the vertical layout exists to avoid; the reader
 * scrolls down the path instead. A roadmap short enough to fit whole is
 * centred vertically too.
 *
 * A map so wide that fitting its width would drop below `READABLE_ZOOM` (a
 * 200-node lesson in a 600px panel opened at ~0.4x) opens at `OPEN_ZOOM` on
 * its top instead - the title and first concepts, centred on the main node -
 * and the reader pans. The Fit button (`FIT_ALL`) still shows it all.
 */
export function openingViewport(
  layout: MindmapLayout,
  width: number,
  height: number
): Viewport {
  const { minX, maxX, minY, maxY } = bounds(layout);
  const fit = Math.max(
    MIN_ZOOM,
    Math.min(MAX_OPEN_ZOOM, (width - PAD * 2) / (maxX - minX))
  );
  if (fit < READABLE_ZOOM) {
    const root = layout.nodes.find(n => n.depth === 0) ?? layout.nodes[0];
    return {
      zoom: OPEN_ZOOM,
      x: width / 2 - root.x * OPEN_ZOOM,
      y: PAD - minY * OPEN_ZOOM,
    };
  }
  const mapHeight = (maxY - minY) * fit;
  const x = width / 2 - ((minX + maxX) / 2) * fit;
  const y =
    mapHeight + PAD * 2 <= height
      ? (height - mapHeight) / 2 - minY * fit
      : PAD - minY * fit;
  return { x, y, zoom: fit };
}

/**
 * Applies `openingViewport`. Re-applied when the layout changes (fonts
 * loading) and when the pane resizes (panel collapse, fullscreen), which also
 * resets the scroll.
 */
export function useRoadmapViewport(
  wrapRef: React.RefObject<HTMLDivElement>,
  layout: MindmapLayout,
  enabled = true
) {
  const flow = useReactFlow();

  useEffect(() => {
    const el = wrapRef.current;
    if (!el || !enabled) return;
    const open = () => {
      const { width, height } = el.getBoundingClientRect();
      if (!width || !height || !layout.nodes.length) return;
      flow.setViewport(openingViewport(layout, width, height));
    };

    let frame = requestAnimationFrame(open);
    if (typeof ResizeObserver === 'undefined') {
      return () => cancelAnimationFrame(frame);
    }
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(open);
    });
    observer.observe(el);
    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
    };
  }, [flow, wrapRef, layout, enabled]);
}

/** Room kept beside a preview's outermost (faint) nodes. */
const PREVIEW_SIDE = 12;
/** A preview may grow a little past 1x when its box is roomy. */
const PREVIEW_MAX_ZOOM = 1.15;
/** Below this the pills' 11-12px labels stop being readable. */
const PREVIEW_MIN_READABLE = 0.62;

function previewFit(layout: MindmapLayout, width: number, height: number) {
  // Faint nodes included: they are drawn, and must sit inside the box.
  const b = bounds(layout);
  const zoom = Math.max(
    MIN_ZOOM,
    Math.min(
      PREVIEW_MAX_ZOOM,
      (width - PAD) / (b.maxX - b.minX + PREVIEW_SIDE * 2),
      (height - PAD * 2) / (b.maxY - b.minY)
    )
  );
  return {
    zoom,
    x: width / 2 - ((b.minX + b.maxX) / 2) * zoom,
    y: height / 2 - ((b.minY + b.maxY) / 2) * zoom,
  };
}

/**
 * A preview's view (`MindmapCanvas` `preview`): every node - the faint next
 * level too - fitted WHOLE into the box, width and height, centred. Nothing
 * scrolls, so the whole outline is on screen at once.
 *
 * `layouts` run from most to least detailed. The
 * view uses the first whose fit keeps the labels at `PREVIEW_MIN_READABLE` or
 * more (else the last) and reports it (`onPick`) so the canvas draws that
 * layout. Re-fitted on resize and when the layouts change.
 */
export function usePreviewViewport(
  wrapRef: React.RefObject<HTMLDivElement>,
  layouts: MindmapLayout[],
  { enabled, onPick }: { enabled: boolean; onPick: (index: number) => void }
) {
  const flow = useReactFlow();

  useEffect(() => {
    const el = wrapRef.current;
    if (!el || !enabled || !layouts.length) return;
    const fit = () => {
      const { width, height } = el.getBoundingClientRect();
      if (!width || !height || !layouts.length) return;
      const fits = layouts.map(l => previewFit(l, width, height));
      const readable = fits.findIndex(f => f.zoom >= PREVIEW_MIN_READABLE);
      const index = readable === -1 ? layouts.length - 1 : readable;
      onPick(index);
      flow.setViewport(fits[index]);
    };

    let frame = requestAnimationFrame(fit);
    if (typeof ResizeObserver === 'undefined') {
      return () => cancelAnimationFrame(frame);
    }
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(fit);
    });
    observer.observe(el);
    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
    };
  }, [flow, wrapRef, layouts, enabled, onPick]);
}
